import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { ConfigurationSelector } from 'store/configuration';
import { ThunkInitiateCallScribeCall } from 'store/callScribe/thunks';
import { isContactScreen } from 'components/CallScribe/contactScreen';
import { isEmbeddedInIframe } from 'components/CallScribe/popout';
import { type IPopupBusy, openCallPopup } from 'components/CallScribe/startPopupCall';
import { subscribeToCallStateSignal } from 'components/CallScribe/callEndSignal';
import { isSameNumber } from 'components/CallScribe/softphoneLiveCall';
import { formatPhoneE164ToDisplay } from 'components/CallScribe/Recents/recentsHelpers';
import {
  acquireLiveCallLock,
  askLiveWindowToFocus,
  readLiveCallHolder,
  releaseLiveCallLock,
  whenLiveCallEnds,
} from 'components/CallScribe/liveCallLease';
import { IUseTwilioDeviceResult } from 'components/CallScribe/useTwilioDevice';
import type { TDialLifecycleEvent } from 'components/CallScribe/Home/CallScribeHome';

export type TPlaceOutbound = (
  to: string,
  contactExternalId?: string,
  contactName?: string,
  companyOverride?: string,
) => void;

export interface IUsePlaceOutboundArgs {
  twilio: IUseTwilioDeviceResult;
  companyId: string | number | null | undefined;
  onDialLifecycle?: (event: TDialLifecycleEvent) => void;
}

export interface IUsePlaceOutboundResult {
  placeOutbound: TPlaceOutbound;
  dialFailed: boolean;
  busyWith: IPopupBusy | null;
  noteBusy: (busy: IPopupBusy) => void;
}

export const usePlaceOutbound = ({
  twilio,
  companyId,
  onDialLifecycle,
}: IUsePlaceOutboundArgs): IUsePlaceOutboundResult => {
  const dispatch = useAppDispatch();
  const config = useAppSelector(ConfigurationSelector);
  const contactScreen = isContactScreen(config);
  const [dialFailed, setDialFailed] = useState(false);
  const [busyWith, setBusyWith] = useState<IPopupBusy | null>(null);

  useEffect(() => {
    if (busyWith === null) return undefined;
    return subscribeToCallStateSignal(() => { setBusyWith(null); });
  }, [busyWith]);

  const noteBusy = useCallback((busy: IPopupBusy) => { setBusyWith(busy); }, []);

  const [dialing, setDialing] = useState(false);
  const callUp = twilio.activeCall !== undefined ||
    twilio.state === 'connecting' ||
    twilio.state === 'in-call';
  const callUpRef = useRef(callUp);
  callUpRef.current = callUp;
  const placingRef = useRef(false);

  useEffect(() => {
    if (!dialing && !callUp) releaseLiveCallLock();
  }, [dialing, callUp]);

  const stopWaitingRef = useRef<(() => void) | null>(null);
  useEffect(() => () => { stopWaitingRef.current?.(); }, []);

  const holdForOtherWindow = useCallback((to: string) => {
    const holder = readLiveCallHolder();
    setBusyWith({
      busyWith: holder ? holder.callee.trim() || formatPhoneE164ToDisplay(holder.toNumber) || holder.toNumber : '',
      sameCall: holder !== null && isSameNumber(holder.toNumber, to),
    });
    askLiveWindowToFocus();
    stopWaitingRef.current?.();
    stopWaitingRef.current = whenLiveCallEnds(() => { setBusyWith(null); });
  }, []);

  const placeOutbound = useCallback<TPlaceOutbound>((
    to,
    contactExternalId,
    contactName,
    companyOverride,
  ) => {
    const companyExternalId = companyOverride ??
      (companyId != null ? String(companyId) : undefined);

    if (isEmbeddedInIframe()) {
      const result = openCallPopup({
        to,
        psaId: config.psaId ?? null,
        ticketId: contactScreen ? null : config.ticketId ?? null,
        contactExternalId,
        companyExternalId,
        contactName,
      });
      setBusyWith(result.opened ? null : { busyWith: result.busyWith, sameCall: result.sameCall });
      return;
    }

    if (placingRef.current || callUpRef.current) return;
    placingRef.current = true;
    setDialFailed(false);
    setDialing(true);
    void (async () => {
      let startedCallId: number | undefined;
      const callee = contactName?.trim() || formatPhoneE164ToDisplay(to) || to;
      if (!(await acquireLiveCallLock({ callId: null, callee, toNumber: to }))) {
        placingRef.current = false;
        setDialing(false);
        holdForOtherWindow(to);
        return;
      }
      setBusyWith(null);
      try {
        const result = await dispatch(
          ThunkInitiateCallScribeCall({
            fromNumber: '',
            toNumber: to,
            crmSourceId: config.psaId ?? null,
            externalTicketId: contactScreen ? null : config.ticketId ?? null,
            companyExternalId: companyExternalId ?? null,
            contactExternalId: contactExternalId ?? null,
          }),
        ).unwrap();
        startedCallId = result.id;
        onDialLifecycle?.({
          type: 'started',
          callId: result.id,
          to,
          contactName,
          documentationNotice: result.documentationUnavailableReason ?? undefined,
        });
        await twilio.placeCall({ to, callId: result.id });
      } catch (err) {
        releaseLiveCallLock();
        setDialFailed(true);
        onDialLifecycle?.({
          type: 'failed',
          to,
          callId: startedCallId,
          message: err instanceof Error ? err.message : undefined,
        });
      } finally {
        placingRef.current = false;
        setDialing(false);
      }
    })();
  }, [config.psaId, config.ticketId, dispatch, companyId, twilio, onDialLifecycle, holdForOtherWindow]);

  return { placeOutbound, dialFailed, busyWith, noteBusy };
};
